// 独立站 AI 流水线 demo - mock 数据
// 14 表全量 mock，全部写死，不调任何后端 API

import {
  AiCandidate, AiCandidateRef, AiCandidateDecision, AiCandidateQuality,
  AiListingDraft, AiPriceRecommendation, AiCandidateEffectDaily,
  IdpEvent, IdpPage, IdpElement, IdpExperiment, IdpVariant, IdpExposure,
  PageDailyRollup, ClickDailyRollup, ScrollDailyRollup, ExperimentDailyRollup,
  LifecycleStatus, SiteId, SourceType, StyleTag, RejectReasonCode, EventType, DeviceType,
  IdpSessionAgg, IdpCohortRetention, IdpDataHealth, IdpSignificanceResult, FunnelStep,
  PriceLearningSnapshot, ListingLearningSnapshot, ImageLearningSnapshot, EffectFeedbackSnapshot,
  ListingRecallEntry,
} from './types';

// ============================================================
// 基础常量
// ============================================================

const STYLES: StyleTag[] = ['哥特', '暗黑', '森系', '古典', '极简', '复古'];
const SITES: SiteId[] = [0, 1, 2];
const SITE_NAMES: Record<SiteId, string> = { 0: 'FG', 1: 'ST', 2: 'BF' };
const SOURCES: SourceType[] = ['TREND', 'STORE_SCAN', 'COMPETITOR', 'INQUIRY_TOPIC'];
const MODELS = ['NB Pro', 'NB2', 'Flux 2 + jewelry LoRA', 'GPT Image 2'];
const OPERATORS = [
  { id: 'op001', name: '王运营' },
  { id: 'op002', name: '李设计' },
  { id: 'op003', name: '张运营' },
  { id: 'op004', name: '陈采购' },
];

// 真实图片 URL — 用珠宝商品占位图（OSS 假地址）
const MOCK_IMAGES = [
  'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400&q=80',
  'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=400&q=80',
  'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=400&q=80',
  'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&q=80',
  'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=400&q=80',
  'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&q=80',
  'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=400&q=80',
  'https://images.unsplash.com/photo-1617038220319-276d3cfab638?w=400&q=80',
  'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=400&q=80',
  'https://images.unsplash.com/photo-1633934542430-0905d5b97da6?w=400&q=80',
];

// 状态机 10 +REJECTED+ARCHIVED
const ALL_STATUSES: LifecycleStatus[] = [
  'DISCOVERED', 'SELECTED', 'IMAGE_GENERATING', 'IMAGE_REVIEW',
  'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED',
  'REJECTED', 'ARCHIVED'
];

const STATUS_LABELS: Record<LifecycleStatus, string> = {
  DISCOVERED: '已发现',
  SELECTED: '已入池',
  IMAGE_GENERATING: '生图中',
  IMAGE_REVIEW: '待审图',
  LISTING_DRAFT: '草稿撰写',
  PRICED: '已定价',
  SHOPIFY_DRAFT: 'Shopify 草稿',
  PUBLISHED: '已发布',
  MEASURING: '观察中',
  LEARNED: '复盘完成',
  REJECTED: '已拒绝',
  ARCHIVED: '已归档',
};

const STATUS_COLORS: Record<LifecycleStatus, string> = {
  DISCOVERED: '#8c8c8c',
  SELECTED: '#1890ff',
  IMAGE_GENERATING: '#13c2c2',
  IMAGE_REVIEW: '#fa8c16',
  LISTING_DRAFT: '#722ed1',
  PRICED: '#eb2f96',
  SHOPIFY_DRAFT: '#52c41a',
  PUBLISHED: '#389e0d',
  MEASURING: '#fadb14',
  LEARNED: '#52c41a',
  REJECTED: '#f5222d',
  ARCHIVED: '#bfbfbf',
};

const REJECT_REASON_LABELS: Record<RejectReasonCode, string> = {
  BLURRY: '模糊不清',
  CHAOTIC_RING: '戒环混乱',
  COLOR_OFF: '色彩偏差',
  HAND_DEFORMED: '手部畸形',
  PROP_DEFECT: '道具/配饰缺陷',
  STYLE_MISMATCH: '风格不符',
  STONE_OFF: '宝石位置错',
  BACKGROUND_BAD: '背景不合',
  OTHER: '其他',
};

export { STATUS_LABELS, STATUS_COLORS, REJECT_REASON_LABELS, SITE_NAMES };

// ============================================================
// 辅助函数
// ============================================================

let _seed = 42;
function pseudoRandom() {
  _seed = (_seed * 1103515245 + 12345) & 0x7fffffff;
  return _seed / 0x7fffffff;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(pseudoRandom() * arr.length)];
}

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

function daysAgo(d: number): string {
  const t = new Date(2026, 5, 27);
  t.setDate(t.getDate() - d);
  return t.toISOString();
}

function nowMinus(hours: number): string {
  const t = new Date(2026, 5, 27, 9, 0, 0);
  t.setHours(t.getHours() - hours);
  return t.toISOString();
}

// ============================================================
// 1. AI 候选 30 条（覆盖所有状态）
// ============================================================

// 分布：每状态最少 2 条
const STATUS_DISTRIBUTION: Array<[LifecycleStatus, number]> = [
  ['DISCOVERED', 3],
  ['SELECTED', 3],
  ['IMAGE_GENERATING', 2],
  ['IMAGE_REVIEW', 5],
  ['LISTING_DRAFT', 2],
  ['PRICED', 4],
  ['SHOPIFY_DRAFT', 2],
  ['PUBLISHED', 2],
  ['MEASURING', 3],
  ['LEARNED', 3],
  ['REJECTED', 1],
];

const TITLES = [
  '复古黄铜大教堂尖顶戒指',
  '哥特黑钻十字架吊坠',
  '森系叶脉树叶项链',
  '古典珍珠葡萄串耳坠',
  '极简纯银几何方块戒指',
  '暗黑骷髅头宽幅戒指',
  '复古玫瑰金叶纹手镯',
  '哥特蝙蝠翼银戒',
  '森系蘑菇蓝宝石胸针',
  '古典维多利亚浮雕项链',
  '极简钛钢曲面耳钉',
  '暗黑曜石黑曜镶嵌戒指',
  '复古铜质钥匙吊坠',
  '哥特玫瑰刺藤手链',
  '森系木纹翡翠戒环',
  '古典金线编织手镯',
  '极简黑色矩阵戒指',
  '暗黑死神镰刀吊坠',
  '复古怀表式项链坠',
  '哥特尖塔图腾戒指',
  '森系松果琥珀吊坠',
  '古典刻纹银手镯',
  '极简纯金窄环戒',
  '暗黑十字十二宫戒',
  '复古金属雕花耳坠',
  '哥特蜘蛛网耳钉',
  '森系蕨类植物胸针',
  '古典珍珠贝壳吊坠',
  '极简纯钛对戒',
  '暗黑骨骼花纹手链',
];

export const aiCandidates: AiCandidate[] = (() => {
  const list: AiCandidate[] = [];
  let idx = 0;
  for (const [status, count] of STATUS_DISTRIBUTION) {
    for (let i = 0; i < count; i++) {
      const created = pseudoRandom() * 40 + 1; // 1-41 天前发现
      const updated = Math.max(0, created - pseudoRandom() * 5);
      const c: AiCandidate = {
        candidate_id: `cand_${String(idx + 1).padStart(4, '0')}`,
        eps_id: 2,
        site_id: pick(SITES),
        style_id: STYLES[idx % STYLES.length],
        source_type: pick(SOURCES),
        source_ref_id: `ref_${idx}_${Math.floor(pseudoRandom() * 100000)}`,
        status,
        batch_no: `B-2026-06-${String(Math.floor(idx / 5) + 1).padStart(2, '0')}`,
        owner_operator_id: pick(OPERATORS).id,
        owner_name: pick(OPERATORS).name,
        created_by_ai_model: pick(MODELS),
        prompt_version: 'v1.7',
        thumbnail_url: MOCK_IMAGES[idx % MOCK_IMAGES.length],
        title: TITLES[idx % TITLES.length],
        created_at: daysAgo(created),
        updated_at: daysAgo(updated),
      };
      if (status === 'LEARNED') {
        c.outcome = pick(['HIT', 'MISS', 'PARTIAL'] as const);
      }
      if (status === 'REJECTED') {
        c.risk_tag = ['style_mismatch'];
      }
      list.push(c);
      idx++;
    }
  }
  return list;
})();

// ============================================================
// 2. 候选引用关系
// ============================================================

export const aiCandidateRefs: AiCandidateRef[] = (() => {
  const list: AiCandidateRef[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    // 每个候选都引用 1 个 commodity（来源）
    list.push({
      id: `ref_${++id}`,
      candidate_id: c.candidate_id,
      ref_type: 'COMMODITY',
      ref_id: `eps_commodity_${Math.floor(pseudoRandom() * 100000)}`,
      ref_snapshot: { title: c.title + '（源款）', commodity_no: 'ZB' + Math.floor(pseudoRandom() * 1000000) },
      created_at: c.created_at,
    });
    // 进入生图后才有 image_batch ref
    if (['IMAGE_GENERATING', 'IMAGE_REVIEW', 'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status)) {
      list.push({
        id: `ref_${++id}`,
        candidate_id: c.candidate_id,
        ref_type: 'IMAGE_BATCH',
        ref_id: `batch_${Math.floor(pseudoRandom() * 100000)}`,
        ref_snapshot: { sg_batch_id: 'sg_' + Math.floor(pseudoRandom() * 100000), candidate_count: 5 },
        created_at: c.updated_at,
      });
    }
    // 进入 Shopify 后才有 product ref
    if (['SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status)) {
      list.push({
        id: `ref_${++id}`,
        candidate_id: c.candidate_id,
        ref_type: 'SHOPIFY_PRODUCT',
        ref_id: `shopify_prod_${Math.floor(pseudoRandom() * 100000)}`,
        ref_snapshot: { gid: 'gid://shopify/Product/' + Math.floor(pseudoRandom() * 100000) },
        created_at: c.updated_at,
      });
    }
    // 已发布的有订单引用
    if (['MEASURING', 'LEARNED'].includes(c.status) && pseudoRandom() > 0.5) {
      list.push({
        id: `ref_${++id}`,
        candidate_id: c.candidate_id,
        ref_type: 'ORDER',
        ref_id: `order_${Math.floor(pseudoRandom() * 100000)}`,
        ref_snapshot: { order_no: '#FG' + Math.floor(pseudoRandom() * 10000) },
        created_at: c.updated_at,
      });
    }
  }
  return list;
})();

// ============================================================
// 3. 决策审计流
// ============================================================

const STATUS_PROGRESSION: LifecycleStatus[] = [
  'DISCOVERED', 'SELECTED', 'IMAGE_GENERATING', 'IMAGE_REVIEW',
  'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'
];

const DECISION_BY_TRANSITION: Record<string, { decision_type: any, reason?: string }> = {
  'DISCOVERED->SELECTED': { decision_type: 'SELECT' },
  'SELECTED->IMAGE_GENERATING': { decision_type: 'SELECT' },
  'IMAGE_GENERATING->IMAGE_REVIEW': { decision_type: 'SELECT' },
  'IMAGE_REVIEW->LISTING_DRAFT': { decision_type: 'SELECT' },
  'LISTING_DRAFT->PRICED': { decision_type: 'EDIT_LISTING', reason: '运营调整标题措辞' },
  'PRICED->SHOPIFY_DRAFT': { decision_type: 'EDIT_PRICE', reason: '采纳建议价' },
  'SHOPIFY_DRAFT->PUBLISHED': { decision_type: 'PUBLISH' },
  'PUBLISHED->MEASURING': { decision_type: 'SELECT' },
  'MEASURING->LEARNED': { decision_type: 'SELECT' },
};

export const aiCandidateDecisions: AiCandidateDecision[] = (() => {
  const list: AiCandidateDecision[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    const currentIdx = STATUS_PROGRESSION.indexOf(c.status);
    if (currentIdx < 0) {
      // REJECTED 走特殊路径
      if (c.status === 'REJECTED') {
        list.push({
          id: `dec_${++id}`,
          candidate_id: c.candidate_id,
          decision_type: 'SELECT',
          operator_id: 'op001',
          operator_name: '王运营',
          before_status: 'DISCOVERED',
          after_status: 'IMAGE_REVIEW',
          decided_at: c.created_at,
        });
        list.push({
          id: `dec_${++id}`,
          candidate_id: c.candidate_id,
          decision_type: 'REJECT',
          operator_id: 'op001',
          operator_name: '王运营',
          reason_code: 'STYLE_MISMATCH',
          reason_text: '色调与品牌风格冲突，AI 多次重抽仍偏色',
          before_status: 'IMAGE_REVIEW',
          after_status: 'REJECTED',
          decided_at: c.updated_at,
        });
      }
      continue;
    }
    // 走完整路径
    for (let i = 0; i < currentIdx; i++) {
      const before = STATUS_PROGRESSION[i];
      const after = STATUS_PROGRESSION[i + 1];
      const key = `${before}->${after}`;
      const transition = DECISION_BY_TRANSITION[key];
      if (transition) {
        const op = pick(OPERATORS);
        const hoursAgo = (currentIdx - i) * 3;
        list.push({
          id: `dec_${++id}`,
          candidate_id: c.candidate_id,
          decision_type: transition.decision_type,
          operator_id: op.id,
          operator_name: op.name,
          reason_text: transition.reason,
          before_status: before,
          after_status: after,
          decided_at: nowMinus(hoursAgo),
        });
      }
    }
  }
  return list;
})();

// ============================================================
// 4. 图候选质量（每个 IMAGE_REVIEW 及后续状态的候选都有 4-6 张候选图）
// ============================================================

const REJECT_TAGS_ALL: RejectReasonCode[] = [
  'BLURRY', 'CHAOTIC_RING', 'COLOR_OFF', 'HAND_DEFORMED',
  'PROP_DEFECT', 'STYLE_MISMATCH', 'STONE_OFF', 'BACKGROUND_BAD'
];

export const aiCandidateQualities: AiCandidateQuality[] = (() => {
  const list: AiCandidateQuality[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    const hasImages = ['IMAGE_REVIEW', 'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status);
    if (!hasImages) continue;
    const total = c.status === 'IMAGE_REVIEW' ? 6 : 4;
    let selected = false;
    for (let i = 0; i < total; i++) {
      const score = Math.floor(40 + pseudoRandom() * 55);
      const cv_filtered = score < 60;
      const is_selected = !cv_filtered && !selected && i > 1;
      if (is_selected) selected = true;
      const tags = cv_filtered ? [pick(REJECT_TAGS_ALL)] : (pseudoRandom() > 0.5 ? [pick(REJECT_TAGS_ALL)] : []);
      list.push({
        id: `q_${++id}`,
        candidate_id: c.candidate_id,
        image_id: `img_${id}`,
        image_url: MOCK_IMAGES[(id + i) % MOCK_IMAGES.length],
        quality_score: score,
        cv_filtered,
        cv_filter_reason: cv_filtered ? '清晰度<60，自动剔除' : undefined,
        reject_tags: tags as RejectReasonCode[],
        visual_consistency_score: Math.floor(50 + pseudoRandom() * 50),
        stone_position_accuracy: Math.floor(50 + pseudoRandom() * 50),
        background_fit_score: Math.floor(50 + pseudoRandom() * 50),
        is_selected,
        attempt_no: i + 1,
        created_at: nowMinus(i * 2),
      });
    }
  }
  return list;
})();

// ============================================================
// 5. listing 草稿版本（每个 LISTING_DRAFT 及后续状态的候选都有 1-3 个版本）
// ============================================================

export const aiListingDrafts: AiListingDraft[] = (() => {
  const list: AiListingDraft[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    const hasListing = ['LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status);
    if (!hasListing) continue;
    const versions = Math.floor(pseudoRandom() * 2) + 1;
    for (let v = 1; v <= versions; v++) {
      list.push({
        id: `ld_${++id}`,
        candidate_id: c.candidate_id,
        version_no: v,
        title: c.title + (v === 1 ? '' : ' - 14K Solid Gold'),
        handle: c.title.toLowerCase().replace(/\s+/g, '-').slice(0, 50),
        description_html: `<p>Handcrafted ${c.style_id} style ring, featuring intricate details inspired by ${c.style_id === '哥特' ? 'Gothic architecture' : 'natural elements'}. Made with premium materials.</p><ul><li>Material: 14K Solid Gold</li><li>Stone: Natural diamond</li><li>Style: ${c.style_id}</li></ul>`,
        seo_title: c.title + ' | Felicegals',
        seo_desc: `Discover the unique ${c.style_id} style ring at Felicegals. Premium craftsmanship.`,
        tags: [c.style_id, 'ring', '14k', 'gold'],
        collections: [c.style_id + '系列', '新品'],
        metafields: { material: '14K Gold', origin: 'Made in PRC' },
        prompt_version: 'v1.7',
        model_version: 'deepseek-v4-flash',
        generated_at: nowMinus(versions - v + 1),
      });
    }
  }
  return list;
})();

// ============================================================
// 6. 三档价推荐
// ============================================================

export const aiPriceRecommendations: AiPriceRecommendation[] = (() => {
  const list: AiPriceRecommendation[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    const hasPrice = ['PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status);
    if (!hasPrice) continue;
    const baseCents = Math.floor(8000 + pseudoRandom() * 50000);
    list.push({
      id: `pr_${++id}`,
      candidate_id: c.candidate_id,
      cost_assumptions: {
        grain_cost_cents: Math.floor(baseCents * 0.3),
        pure_gold_weight_g: parseFloat((1 + pseudoRandom() * 4).toFixed(2)),
        hand_fee_cents: Math.floor(baseCents * 0.05),
        main_stone_value_cents: Math.floor(baseCents * 0.15),
        slave_stone_value_cents: Math.floor(baseCents * 0.03),
      },
      similar_items: [
        { ref_id: `eps_commodity_${Math.floor(pseudoRandom() * 100000)}`, similarity: 0.92, price_cents: baseCents + 500, multiple: 3.2 },
        { ref_id: `eps_commodity_${Math.floor(pseudoRandom() * 100000)}`, similarity: 0.87, price_cents: baseCents - 800, multiple: 3.0 },
        { ref_id: `eps_commodity_${Math.floor(pseudoRandom() * 100000)}`, similarity: 0.81, price_cents: baseCents + 1500, multiple: 3.4 },
      ],
      conservative_price_cents: Math.floor(baseCents * 0.85),
      recommended_price_cents: baseCents,
      test_price_cents: Math.floor(baseCents * 1.15),
      confidence: parseFloat((0.6 + pseudoRandom() * 0.35).toFixed(2)),
      gold_price_at_calc: 530,
      exchange_rate_at_calc: 7.18,
      multiple_used: 3.2,
      created_at: nowMinus(4),
    });
  }
  return list;
})();

// ============================================================
// 7. 效果时序（已发布候选才有）
// ============================================================

export const aiCandidateEffectDailies: AiCandidateEffectDaily[] = (() => {
  const list: AiCandidateEffectDaily[] = [];
  let id = 0;
  for (const c of aiCandidates) {
    const hasEffect = ['MEASURING', 'LEARNED'].includes(c.status);
    if (!hasEffect) continue;
    const days = c.status === 'LEARNED' ? 30 : 14;
    for (let d = 0; d < days; d++) {
      const pv = Math.floor(50 + pseudoRandom() * 200);
      const uv = Math.floor(pv * 0.7);
      const atc = Math.floor(uv * (pseudoRandom() * 0.15 + 0.05));
      const co = Math.floor(atc * (pseudoRandom() * 0.3 + 0.2));
      const purchased = Math.floor(co * (pseudoRandom() * 0.4 + 0.3));
      list.push({
        id: `eff_${++id}`,
        candidate_id: c.candidate_id,
        effect_date: daysAgo(d),
        pv,
        unique_visitor: uv,
        add_to_cart: atc,
        checkout_start: co,
        purchased,
        inquiry_opened: Math.floor(pseudoRandom() * 3),
        returned: pseudoRandom() > 0.85 ? 1 : 0,
      });
    }
  }
  return list;
})();

// ============================================================
// 流量监控 7 表
// ============================================================

// 8. 页面维表
export const idpPages: IdpPage[] = [
  { id: 1, url_hash: 'h1', url: '/', page_type: 'HOMEPAGE', title: '首页' },
  { id: 2, url_hash: 'h2', url: '/collections/gothic', page_type: 'COLLECTION', title: '哥特系列' },
  { id: 3, url_hash: 'h3', url: '/collections/dark', page_type: 'COLLECTION', title: '暗黑系列' },
  { id: 4, url_hash: 'h4', url: '/collections/forest', page_type: 'COLLECTION', title: '森系系列' },
  { id: 5, url_hash: 'h5', url: '/products/gothic-cross-pendant', page_type: 'PDP', title: '哥特十字架吊坠' },
  { id: 6, url_hash: 'h6', url: '/products/dark-skull-ring', page_type: 'PDP', title: '暗黑骷髅戒指' },
  { id: 7, url_hash: 'h7', url: '/products/forest-leaf-necklace', page_type: 'PDP', title: '森系叶脉项链' },
  { id: 8, url_hash: 'h8', url: '/products/classic-pearl-earring', page_type: 'PDP', title: '古典珍珠耳坠' },
  { id: 9, url_hash: 'h9', url: '/cart', page_type: 'CART', title: '购物车' },
  { id: 10, url_hash: 'h10', url: '/checkout', page_type: 'CHECKOUT', title: '结账' },
  { id: 11, url_hash: 'h11', url: '/blog/gemstone-guide', page_type: 'BLOG', title: '宝石鉴别指南' },
  { id: 12, url_hash: 'h12', url: '/about', page_type: 'CONTENT', title: '关于我们' },
];

// 9. 元素维表
export const idpElements: IdpElement[] = [
  { id: 1, selector_hash: 'e1', selector: '.product-image-main', element_type: 'img', semantic_role: 'product_image' },
  { id: 2, selector_hash: 'e2', selector: '.add-to-cart-button', element_type: 'button', semantic_role: 'add_to_cart' },
  { id: 3, selector_hash: 'e3', selector: '.size-selector', element_type: 'select', semantic_role: 'size_picker' },
  { id: 4, selector_hash: 'e4', selector: '.material-selector', element_type: 'select', semantic_role: 'material_picker' },
  { id: 5, selector_hash: 'e5', selector: '.image-gallery-thumbnail', element_type: 'img', semantic_role: 'image_thumb' },
  { id: 6, selector_hash: 'e6', selector: '.checkout-button', element_type: 'button', semantic_role: 'checkout' },
  { id: 7, selector_hash: 'e7', selector: '.product-description', element_type: 'div', semantic_role: 'description' },
  { id: 8, selector_hash: 'e8', selector: '.product-care', element_type: 'div', semantic_role: 'care_guide' },
  { id: 9, selector_hash: 'e9', selector: '.related-products', element_type: 'div', semantic_role: 'related' },
  { id: 10, selector_hash: 'e10', selector: '.size-guide-link', element_type: 'a', semantic_role: 'size_guide' },
  { id: 11, selector_hash: 'e11', selector: '.coupon-banner', element_type: 'div', semantic_role: 'promo' },
  { id: 12, selector_hash: 'e12', selector: '.video-play', element_type: 'button', semantic_role: 'video' },
];

// 11. 实验（在 idpEvents 之前定义，避免 IIFE forward reference）
export const idpExperiments: IdpExperiment[] = [
  {
    experiment_id: 'exp001',
    experiment_key: 'pdp_price_format',
    name: 'PDP 价格展示样式 A/B',
    description: '对比"$199.00"vs"$199（限时）"两种价格格式对加购率的影响',
    status: 'RUNNING',
    start_at: daysAgo(14),
  },
  {
    experiment_id: 'exp002',
    experiment_key: 'pdp_hero_image_style',
    name: 'PDP 主图风格 A/B',
    description: '对比白底 vs lifestyle 佩戴图作为主图的转化率差异',
    status: 'RUNNING',
    start_at: daysAgo(10),
  },
  {
    experiment_id: 'exp003',
    experiment_key: 'description_length',
    name: '描述长短 A/B',
    description: '短描述（< 150 字）vs 长描述（> 300 字含工艺细节）',
    status: 'RUNNING',
    start_at: daysAgo(7),
  },
];

// 12. 变体
export const idpVariants: IdpVariant[] = [
  { variant_id: 'var_001a', experiment_id: 'exp001', variant_key: 'control', name: '原样式 $199.00', allocation: 0.5 },
  { variant_id: 'var_001b', experiment_id: 'exp001', variant_key: 'treatment', name: '新样式 $199（限时）', allocation: 0.5 },
  { variant_id: 'var_002a', experiment_id: 'exp002', variant_key: 'control', name: '白底主图', allocation: 0.5 },
  { variant_id: 'var_002b', experiment_id: 'exp002', variant_key: 'treatment', name: 'lifestyle 佩戴图', allocation: 0.5 },
  { variant_id: 'var_003a', experiment_id: 'exp003', variant_key: 'control', name: '短描述', allocation: 0.5 },
  { variant_id: 'var_003b', experiment_id: 'exp003', variant_key: 'treatment', name: '长描述', allocation: 0.5 },
];

// 10. 事件流 — 300 session 路径生成（重写：session 内事件成路径，不再撒胡椒粉）
const DEVICE_POOL = range(180).map(i => `dev_${String(i).padStart(4, '0')}`);
const CUSTOMER_POOL = range(60).map(i => `cust_${String(i).padStart(4, '0')}`);
const DEVICE_TYPES: DeviceType[] = ['mobile', 'desktop', 'tablet'];
const DEVICE_TYPE_WEIGHTS = [0.55, 0.40, 0.05]; // 移动为主

// 路径转移概率
const TRANSITION: Record<string, Array<{ next: string; prob: number }>> = {
  HOMEPAGE: [
    { next: 'COLLECTION', prob: 0.55 },
    { next: 'PDP', prob: 0.20 },
    { next: 'BLOG', prob: 0.10 },
    { next: 'EXIT', prob: 0.15 },
  ],
  COLLECTION: [
    { next: 'PDP', prob: 0.60 },
    { next: 'COLLECTION', prob: 0.12 },
    { next: 'EXIT', prob: 0.28 },
  ],
  PDP: [
    { next: 'PDP', prob: 0.25 },
    { next: 'ATC', prob: 0.12 },
    { next: 'COLLECTION', prob: 0.08 },
    { next: 'EXIT', prob: 0.55 },
  ],
  CART: [
    { next: 'CHECKOUT', prob: 0.55 },
    { next: 'PDP', prob: 0.10 },
    { next: 'EXIT', prob: 0.35 },
  ],
  CHECKOUT: [
    { next: 'PURCHASE', prob: 0.65 },
    { next: 'EXIT', prob: 0.35 },
  ],
  BLOG: [
    { next: 'COLLECTION', prob: 0.30 },
    { next: 'PDP', prob: 0.20 },
    { next: 'EXIT', prob: 0.50 },
  ],
};

function transitionFrom(curType: string): string {
  const options = TRANSITION[curType] || [{ next: 'EXIT', prob: 1 }];
  const r = pseudoRandom();
  let cum = 0;
  for (const o of options) {
    cum += o.prob;
    if (r < cum) return o.next;
  }
  return 'EXIT';
}

function pickPageByType(t: string): IdpPage | undefined {
  const filtered = idpPages.filter(p => p.page_type === t);
  if (filtered.length === 0) return undefined;
  return filtered[Math.floor(pseudoRandom() * filtered.length)];
}

function pickDeviceType(): DeviceType {
  const r = pseudoRandom();
  let cum = 0;
  for (let i = 0; i < DEVICE_TYPES.length; i++) {
    cum += DEVICE_TYPE_WEIGHTS[i];
    if (r < cum) return DEVICE_TYPES[i];
  }
  return 'desktop';
}

function viewportWByDevice(dt: DeviceType): number {
  if (dt === 'mobile') return 375 + Math.floor(pseudoRandom() * 60); // 375-435
  if (dt === 'tablet') return 768 + Math.floor(pseudoRandom() * 256);
  return 1280 + Math.floor(pseudoRandom() * 640); // 1280-1920
}

// referrer 池：30% page_view 带 referrer（外站 / 站内 / 自然搜索）
const REFERRER_POOL = [
  'https://www.google.com/',
  'https://www.bing.com/',
  'https://www.instagram.com/felicegals/',
  'https://www.tiktok.com/@felicegals',
  'https://www.pinterest.com/felicegals/',
  '', // direct
];

function pickReferrer(): string | undefined {
  if (pseudoRandom() > 0.3) return undefined;
  const r = REFERRER_POOL[Math.floor(pseudoRandom() * REFERRER_POOL.length)];
  return r === '' ? undefined : r;
}

// 客户端 hash(device_id, exp_key) 确定性分桶（spec：A/B 分流走 hash 不是 random）
function hashStringToBucket(s: string, buckets: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % buckets;
}

export const idpEvents: IdpEvent[] = (() => {
  const list: IdpEvent[] = [];
  const candIds = aiCandidates.filter(c => ['PUBLISHED', 'MEASURING', 'LEARNED'].includes(c.status)).map(c => c.candidate_id);
  const expVariants = idpVariants;

  let evtId = 0;
  const TOTAL_SESSIONS = 300;
  for (let sIdx = 0; sIdx < TOTAL_SESSIONS; sIdx++) {
    const sessionId = `sess_${String(sIdx + 1).padStart(4, '0')}`;
    // 60% 复用 device pool 前 80（回访），40% 新设备
    const reuse = pseudoRandom() < 0.6;
    const deviceId = reuse
      ? DEVICE_POOL[Math.floor(pseudoRandom() * 80)]
      : DEVICE_POOL[80 + Math.floor(pseudoRandom() * 100)];
    const customerId = pseudoRandom() < 0.42 ? CUSTOMER_POOL[Math.floor(pseudoRandom() * CUSTOMER_POOL.length)] : undefined;
    const deviceType = pickDeviceType();
    const vw = viewportWByDevice(deviceType);
    const siteId = pick(SITES);

    // 选实验分桶（一个 session 在每个实验中只有一个 variant）
    const sessionVariants: Record<string, string> = {};
    for (const exp of idpExperiments) {
      const expVars = expVariants.filter(v => v.experiment_id === exp.experiment_id);
      sessionVariants[exp.experiment_id] = expVars[Math.floor(pseudoRandom() * expVars.length)].variant_id;
    }

    // 起始时间：14 天内
    let sessionTimeOffsetDays = pseudoRandom() * 14;
    const baseTime = new Date(2026, 5, 27);
    baseTime.setDate(baseTime.getDate() - Math.floor(sessionTimeOffsetDays));
    baseTime.setHours(Math.floor(pseudoRandom() * 24), Math.floor(pseudoRandom() * 60), 0, 0);
    let curTime = baseTime.getTime();

    // 选入口页类型
    const entryR = pseudoRandom();
    const entryType = entryR < 0.40 ? 'HOMEPAGE'
      : entryR < 0.60 ? 'COLLECTION'
        : entryR < 0.90 ? 'PDP'
          : 'BLOG';

    let curPageType = entryType;
    let stepCount = 0;
    const MAX_STEPS = 15;

    while (stepCount < MAX_STEPS && curPageType !== 'EXIT' && curPageType !== 'PURCHASE') {
      // ATC / CART / CHECKOUT 是动作，非页
      if (curPageType === 'ATC') {
        evtId++;
        list.push({
          id: `evt_${evtId}`,
          event_time: new Date(curTime).toISOString(),
          event_type: 'add_to_cart',
          device_id: deviceId,
          session_id: sessionId,
          customer_id: customerId,
          device_type: deviceType,
          viewport_w: vw,
          site_id: siteId,
          experiment_id: 'exp001',
          variant_id: sessionVariants['exp001'],
          candidate_id: candIds.length > 0 && pseudoRandom() > 0.5 ? pick(candIds) : undefined,
        });
        curTime += 1500 + pseudoRandom() * 3000;
        curPageType = 'CART';
        continue;
      }

      const page = pickPageByType(curPageType);
      if (!page) { curPageType = 'EXIT'; break; }

      // page_view 事件
      const engagement = Math.floor(2000 + pseudoRandom() * 28000);
      evtId++;
      const pvEvent: IdpEvent = {
        id: `evt_${evtId}`,
        event_time: new Date(curTime).toISOString(),
        event_type: 'page_view',
        device_id: deviceId,
        session_id: sessionId,
        customer_id: customerId,
        page_id: page.id,
        device_type: deviceType,
        viewport_w: vw,
        engagement_ms: engagement,
        site_id: siteId,
      };
      if (page.page_type === 'PDP' && candIds.length > 0 && pseudoRandom() > 0.35) {
        pvEvent.candidate_id = pick(candIds);
        pvEvent.experiment_id = 'exp002';
        pvEvent.variant_id = sessionVariants['exp002'];
      }
      list.push(pvEvent);

      // 同页 scroll 事件
      if (pseudoRandom() < 0.55) {
        evtId++;
        list.push({
          id: `evt_${evtId}`,
          event_time: new Date(curTime + 2000 + pseudoRandom() * 5000).toISOString(),
          event_type: 'scroll',
          device_id: deviceId,
          session_id: sessionId,
          customer_id: customerId,
          page_id: page.id,
          device_type: deviceType,
          viewport_w: vw,
          scroll_depth: pick([25, 50, 75, 100]),
          site_id: siteId,
        });
      }

      // 同页 click 事件（1-3 个）
      const clickN = Math.floor(pseudoRandom() * 3);
      for (let c = 0; c < clickN; c++) {
        const el = pick(idpElements);
        evtId++;
        list.push({
          id: `evt_${evtId}`,
          event_time: new Date(curTime + 1500 + pseudoRandom() * engagement).toISOString(),
          event_type: 'click',
          device_id: deviceId,
          session_id: sessionId,
          customer_id: customerId,
          page_id: page.id,
          element_id: el.id,
          device_type: deviceType,
          viewport_w: vw,
          x_pct: parseFloat((pseudoRandom() * 100).toFixed(1)),
          y_pct: parseFloat((pseudoRandom() * 100).toFixed(1)),
          site_id: siteId,
          experiment_id: 'exp003',
          variant_id: sessionVariants['exp003'],
        });
      }

      curTime += engagement;
      curPageType = transitionFrom(curPageType);

      // CHECKOUT 触发 checkout_start
      if (curPageType === 'CHECKOUT') {
        evtId++;
        list.push({
          id: `evt_${evtId}`,
          event_time: new Date(curTime).toISOString(),
          event_type: 'checkout_start',
          device_id: deviceId,
          session_id: sessionId,
          customer_id: customerId,
          device_type: deviceType,
          viewport_w: vw,
          site_id: siteId,
        });
      }
      if (curPageType === 'PURCHASE') {
        evtId++;
        list.push({
          id: `evt_${evtId}`,
          event_time: new Date(curTime).toISOString(),
          event_type: 'purchase',
          device_id: deviceId,
          session_id: sessionId,
          customer_id: customerId,
          device_type: deviceType,
          viewport_w: vw,
          site_id: siteId,
        });
      }

      stepCount++;
    }

    // 部分 session 触发询单
    if (pseudoRandom() < 0.06) {
      evtId++;
      list.push({
        id: `evt_${evtId}`,
        event_time: new Date(curTime + 5000).toISOString(),
        event_type: 'inquiry_open',
        device_id: deviceId,
        session_id: sessionId,
        customer_id: customerId,
        device_type: deviceType,
        viewport_w: vw,
        site_id: siteId,
      });
    }
  }
  return list.sort((a, b) => a.event_time.localeCompare(b.event_time));
})();

// 13. 首曝快照 — spec §4.9 UNIQUE(experiment_id, device_id) 只记首次
// 分桶：客户端 hash(device_id, exp_key) 确定性产生（不是 random）
export const idpExposures: IdpExposure[] = (() => {
  const list: IdpExposure[] = [];
  let id = 0;
  const exposedDevices = DEVICE_POOL.slice(0, 150); // 150 个 device 实际参与实验
  for (const deviceId of exposedDevices) {
    // 模拟该 device 首次访问时间
    const firstSeenDay = Math.floor(pseudoRandom() * 14);
    // 模拟该 device 首次访问的设备/站点（首曝快照用于分桶质量校验）
    const dtSnapshot = pickDeviceType();
    const siteSnapshot = pick(SITES);
    for (const exp of idpExperiments) {
      // 按 spec：客户端 hash(device_id, exp_key) → 0..N-1 分桶
      const variantsForExp = idpVariants.filter(v => v.experiment_id === exp.experiment_id);
      const bucket = hashStringToBucket(`${deviceId}::${exp.experiment_key}`, variantsForExp.length);
      const variant = variantsForExp[bucket];
      // 60% 该 device 在每个实验里都触发了首曝（剩 40% 没访问到相关页面没曝光）
      if (pseudoRandom() < 0.6) {
        id++;
        list.push({
          id: `expo_${id}`,
          experiment_id: exp.experiment_id,
          variant_id: variant.variant_id,
          device_id: deviceId,
          first_exposure_at: daysAgo(firstSeenDay),
          device_type_snapshot: dtSnapshot,
          site_id_snapshot: siteSnapshot,
        });
      }
    }
  }
  return list;
})();

// 14. rollup 表

// page_daily 7 天
export const pageDailyRollups: PageDailyRollup[] = (() => {
  const list: PageDailyRollup[] = [];
  for (const p of idpPages) {
    for (let d = 0; d < 7; d++) {
      const pv = Math.floor(50 + pseudoRandom() * 500 * (p.page_type === 'PDP' ? 1.5 : 1));
      list.push({
        page_id: p.id,
        url: p.url,
        date: daysAgo(d).split('T')[0],
        pv,
        uv: Math.floor(pv * 0.65),
        bounce_rate: parseFloat((pseudoRandom() * 0.5).toFixed(3)),
        avg_engagement_ms: Math.floor(5000 + pseudoRandom() * 20000),
      });
    }
  }
  return list;
})();

// click_daily
export const clickDailyRollups: ClickDailyRollup[] = (() => {
  const list: ClickDailyRollup[] = [];
  for (const p of idpPages.filter(p => p.page_type === 'PDP')) {
    for (const e of idpElements) {
      for (const dt of ['mobile', 'desktop'] as DeviceType[]) {
        for (let d = 0; d < 3; d++) {
          const clicks = Math.floor(pseudoRandom() * 50);
          if (clicks < 3) continue;
          list.push({
            page_id: p.id,
            element_id: e.id,
            date: daysAgo(d).split('T')[0],
            selector: e.selector,
            url: p.url,
            clicks,
            unique_users: Math.floor(clicks * 0.8),
            device_type: dt,
            avg_x_pct: parseFloat((30 + pseudoRandom() * 40).toFixed(1)),
            avg_y_pct: parseFloat((20 + pseudoRandom() * 60).toFixed(1)),
          });
        }
      }
    }
  }
  return list;
})();

// scroll_daily
export const scrollDailyRollups: ScrollDailyRollup[] = (() => {
  const list: ScrollDailyRollup[] = [];
  for (const p of idpPages.filter(p => ['PDP', 'COLLECTION', 'BLOG'].includes(p.page_type))) {
    for (const dt of ['mobile', 'desktop'] as DeviceType[]) {
      for (let d = 0; d < 3; d++) {
        for (const bucket of [25, 50, 75, 100]) {
          const users = bucket === 25 ? Math.floor(100 + pseudoRandom() * 200) : Math.floor((150 - bucket * 1.2) * (0.5 + pseudoRandom() * 0.5));
          list.push({
            page_id: p.id,
            url: p.url,
            date: daysAgo(d).split('T')[0],
            device_type: dt,
            scroll_bucket: bucket,
            users: Math.max(10, users),
          });
        }
      }
    }
  }
  return list;
})();

// experiment_daily
export const experimentDailyRollups: ExperimentDailyRollup[] = (() => {
  const list: ExperimentDailyRollup[] = [];
  for (const v of idpVariants) {
    for (let d = 0; d < 7; d++) {
      const exposure = Math.floor(80 + pseudoRandom() * 150);
      const baseRate = v.variant_key === 'treatment' ? 0.045 : 0.038;
      const conv = Math.floor(exposure * (baseRate + (pseudoRandom() - 0.5) * 0.02));
      list.push({
        experiment_id: v.experiment_id,
        variant_id: v.variant_id,
        date: daysAgo(d).split('T')[0],
        exposure_count: exposure,
        conversion_count: conv,
        conversion_rate: parseFloat((conv / exposure).toFixed(4)),
      });
    }
  }
  return list;
})();

// ============================================================
// 工具函数
// ============================================================

export function getDecisionsByCandidate(candidateId: string): AiCandidateDecision[] {
  return aiCandidateDecisions.filter(d => d.candidate_id === candidateId).sort(
    (a, b) => a.decided_at.localeCompare(b.decided_at)
  );
}

export function getQualitiesByCandidate(candidateId: string): AiCandidateQuality[] {
  return aiCandidateQualities.filter(q => q.candidate_id === candidateId);
}

export function getDraftsByCandidate(candidateId: string): AiListingDraft[] {
  return aiListingDrafts.filter(l => l.candidate_id === candidateId).sort((a, b) => b.version_no - a.version_no);
}

export function getPriceRecByCandidate(candidateId: string): AiPriceRecommendation | undefined {
  return aiPriceRecommendations.find(p => p.candidate_id === candidateId);
}

export function getRefsByCandidate(candidateId: string): AiCandidateRef[] {
  return aiCandidateRefs.filter(r => r.candidate_id === candidateId);
}

export function getEffectsByCandidate(candidateId: string): AiCandidateEffectDaily[] {
  return aiCandidateEffectDailies.filter(e => e.candidate_id === candidateId).sort(
    (a, b) => a.effect_date.localeCompare(b.effect_date)
  );
}

// ============================================================
// 流量分析衍生数据 — 会话聚合 / cohort 留存 / 显著性 / 数据健康
// ============================================================

// 1. 会话聚合（由 idpEvents groupBy session_id 计算）
export const idpSessionAggs: IdpSessionAgg[] = (() => {
  const map: Record<string, IdpEvent[]> = {};
  for (const e of idpEvents) {
    if (!map[e.session_id]) map[e.session_id] = [];
    map[e.session_id].push(e);
  }
  const result: IdpSessionAgg[] = [];
  for (const sessId of Object.keys(map)) {
    const events = map[sessId].sort((a, b) => a.event_time.localeCompare(b.event_time));
    const first = events[0];
    const last = events[events.length - 1];
    const pvs = events.filter(e => e.event_type === 'page_view');
    const path = pvs.map(e => {
      const p = idpPages.find(p => p.id === e.page_id);
      return {
        page_id: e.page_id || 0,
        url: p?.url || '',
        title: p?.title || '',
        arrived_at: e.event_time,
        engagement_ms: e.engagement_ms || 0,
      };
    });
    const exitPage = pvs[pvs.length - 1];
    const exitMeta = exitPage ? idpPages.find(p => p.id === exitPage.page_id) : undefined;
    const entryPage = pvs[0];
    const entryMeta = entryPage ? idpPages.find(p => p.id === entryPage.page_id) : undefined;
    result.push({
      session_id: sessId,
      device_id: first.device_id,
      customer_id: first.customer_id,
      site_id: first.site_id,
      device_type: first.device_type,
      start_at: first.event_time,
      end_at: last.event_time,
      duration_ms: new Date(last.event_time).getTime() - new Date(first.event_time).getTime(),
      page_view_count: pvs.length,
      click_count: events.filter(e => e.event_type === 'click').length,
      page_path: path,
      has_atc: events.some(e => e.event_type === 'add_to_cart'),
      has_purchase: events.some(e => e.event_type === 'purchase'),
      has_inquiry: events.some(e => e.event_type === 'inquiry_open'),
      exit_page_id: exitPage?.page_id || 0,
      exit_page_url: exitMeta?.url || '',
      entry_page_id: entryPage?.page_id || 0,
      entry_page_url: entryMeta?.url || '',
      event_count: events.length,
    });
  }
  return result.sort((a, b) => b.start_at.localeCompare(a.start_at));
})();

// 2. Cohort 留存：8 周 cohort
export const idpCohortRetentions: IdpCohortRetention[] = (() => {
  const list: IdpCohortRetention[] = [];
  const baseDate = new Date(2026, 5, 27); // 2026-06-27
  for (let w = 0; w < 8; w++) {
    const d = new Date(baseDate);
    d.setDate(d.getDate() - (w + 1) * 7);
    const cohortDate = d.toISOString().split('T')[0];
    const cohortSize = Math.floor(220 + pseudoRandom() * 130); // 220-350
    // 留存衰减曲线（哥特/暗黑/森系珠宝独立站，留存偏低）
    list.push({
      cohort_date: cohortDate,
      cohort_size: cohortSize,
      retention: {
        d0: 1.0,
        d1: 0.28 + pseudoRandom() * 0.10,
        d3: 0.16 + pseudoRandom() * 0.08,
        d7: 0.10 + pseudoRandom() * 0.05,
        d14: 0.06 + pseudoRandom() * 0.04,
        d30: 0.035 + pseudoRandom() * 0.025,
      },
    });
  }
  return list;
})();

// 3. 显著性结果：3 个实验各一条
export const idpSignificanceResults: IdpSignificanceResult[] = [
  {
    experiment_id: 'exp001',
    primary_metric: 'add_to_cart_rate',
    control_variant_id: 'var_001a',
    treatment_variant_id: 'var_001b',
    control_n: 1248,
    control_conversions: 47,
    control_rate: 0.0377,
    treatment_n: 1273,
    treatment_conversions: 61,
    treatment_rate: 0.0479,
    lift_abs: 0.0102,
    lift_relative_pct: 27.05,
    p_value: 0.041,
    is_significant: true,
    confidence_interval_95: [1.2, 51.8],
    min_detectable_effect_pct: 18.0,
    has_reached_mde: true,
    bucket_quality: {
      device_type_chi2: 0.84,
      site_chi2: 1.32,
      is_balanced: true,
    },
    power: 0.83,
  },
  {
    experiment_id: 'exp002',
    primary_metric: 'conversion_rate',
    control_variant_id: 'var_002a',
    treatment_variant_id: 'var_002b',
    control_n: 892,
    control_conversions: 21,
    control_rate: 0.0235,
    treatment_n: 917,
    treatment_conversions: 25,
    treatment_rate: 0.0273,
    lift_abs: 0.0038,
    lift_relative_pct: 16.05,
    p_value: 0.487,
    is_significant: false,
    confidence_interval_95: [-26.4, 58.5],
    min_detectable_effect_pct: 25.0,
    has_reached_mde: false,
    bucket_quality: {
      device_type_chi2: 0.72,
      site_chi2: 0.91,
      is_balanced: true,
    },
    power: 0.42,
  },
  {
    experiment_id: 'exp003',
    primary_metric: 'engagement_ms',
    control_variant_id: 'var_003a',
    treatment_variant_id: 'var_003b',
    control_n: 1452,
    control_conversions: 89, // engagement > 30s 用户数
    control_rate: 0.0613,
    treatment_n: 1488,
    treatment_conversions: 134,
    treatment_rate: 0.0901,
    lift_abs: 0.0288,
    lift_relative_pct: 46.95,
    p_value: 0.002,
    is_significant: true,
    confidence_interval_95: [18.4, 75.5],
    min_detectable_effect_pct: 20.0,
    has_reached_mde: true,
    bucket_quality: {
      device_type_chi2: 0.51,
      site_chi2: 0.68,
      is_balanced: true,
    },
    power: 0.92,
  },
];

// 4. 数据健康
export const idpDataHealth: IdpDataHealth = (() => {
  const baseTime = new Date(2026, 5, 27, 14, 0, 0).getTime();
  const lagHistory = (currentLag: number) => {
    const arr: Array<{ ts: string; lag_min: number }> = [];
    for (let h = 0; h < 24; h++) {
      const t = new Date(baseTime - h * 3600 * 1000);
      // 模拟波动
      const lag = Math.max(0, currentLag + (pseudoRandom() - 0.5) * 4);
      arr.push({ ts: t.toISOString(), lag_min: parseFloat(lag.toFixed(2)) });
    }
    return arr.reverse();
  };

  return {
    // spec §4.9 数字化验收 — demo 上 mock 值故意贴近目标但不全过
    spec_acceptance: {
      pv_accuracy_vs_legacy: 0.992, // ✓ 过
      device_id_persistence_7d: 0.948, // ⚠️ 差一点点
      click_full_capture_rate: 0.997, // ✓ 过
      experiment_bucket_consistency_7d: 1.000, // ✓ 过（hash 确定性所以一致性 100%）
    },
    rollup_lag: [
      { table: 'idp_page_daily', current_lag_min: 1.8, sla_min: 5, history_24h: lagHistory(1.8) },
      { table: 'idp_click_daily', current_lag_min: 2.3, sla_min: 5, history_24h: lagHistory(2.3) },
      { table: 'idp_scroll_daily', current_lag_min: 3.1, sla_min: 5, history_24h: lagHistory(3.1) },
      { table: 'idp_experiment_daily', current_lag_min: 6.4, sla_min: 5, history_24h: lagHistory(6.4) }, // 超 SLA
      { table: 'ai_candidate_effect_daily', current_lag_min: 4.2, sla_min: 10, history_24h: lagHistory(4.2) },
    ],
    field_fill_rate: [
      { field: 'event_time', table: 'idp_event', fill_rate: 1.000, total_events: idpEvents.length, expected_threshold: 1.0 },
      { field: 'device_id', table: 'idp_event', fill_rate: 1.000, total_events: idpEvents.length, expected_threshold: 1.0 },
      { field: 'session_id', table: 'idp_event', fill_rate: 1.000, total_events: idpEvents.length, expected_threshold: 0.99 },
      { field: 'customer_id', table: 'idp_event', fill_rate: 0.418, total_events: idpEvents.length, expected_threshold: 0.35 },
      { field: 'page_id', table: 'idp_event', fill_rate: 0.612, total_events: idpEvents.length, expected_threshold: 0.55 },
      { field: 'engagement_ms', table: 'idp_event', fill_rate: 0.598, total_events: idpEvents.length, expected_threshold: 0.55 },
      { field: 'experiment_id', table: 'idp_event', fill_rate: 0.213, total_events: idpEvents.length, expected_threshold: 0.10 },
      { field: 'candidate_id', table: 'idp_event', fill_rate: 0.187, total_events: idpEvents.length, expected_threshold: 0.10 },
      { field: 'viewport_w', table: 'idp_event', fill_rate: 0.997, total_events: idpEvents.length, expected_threshold: 0.95 },
      { field: 'x_pct', table: 'idp_event (click)', fill_rate: 0.987, total_events: idpEvents.filter(e => e.event_type === 'click').length, expected_threshold: 0.95 },
      { field: 'scroll_depth', table: 'idp_event (scroll)', fill_rate: 1.000, total_events: idpEvents.filter(e => e.event_type === 'scroll').length, expected_threshold: 1.0 },
    ],
    partition_status: [
      { partition: '2026_04', status: 'OK', event_count: 1248793, bytes: 1024 * 1024 * 142 },
      { partition: '2026_05', status: 'OK', event_count: 1487205, bytes: 1024 * 1024 * 168 },
      { partition: '2026_06', status: 'OK', event_count: 982451, bytes: 1024 * 1024 * 112 },
      { partition: '2026_07', status: 'PENDING', event_count: 0, bytes: 0 },
    ],
    bot_filter: (() => {
      const arr: IdpDataHealth['bot_filter'] = [];
      for (let d = 0; d < 7; d++) {
        const total = Math.floor(38000 + pseudoRandom() * 8000);
        const bot = Math.floor(total * (0.07 + pseudoRandom() * 0.03));
        const internal = Math.floor(total * (0.02 + pseudoRandom() * 0.01));
        arr.push({
          date: daysAgo(d).split('T')[0],
          total,
          filtered_bot: bot,
          filtered_internal: internal,
          real_users: total - bot - internal,
        });
      }
      return arr.reverse();
    })(),
    dim_cache: [
      { dim: 'idp_page', hit: 1248203, miss: 8421, hit_rate: 0.9933, p99_lookup_us: 12 },
      { dim: 'idp_element', hit: 3148201, miss: 47821, hit_rate: 0.9850, p99_lookup_us: 18 },
      { dim: 'idp_session (lru)', hit: 894521, miss: 124892, hit_rate: 0.8775, p99_lookup_us: 145 },
    ],
    beacon_queue: {
      p50_ms: 8,
      p95_ms: 42,
      p99_ms: 138,
      queue_depth: 14,
      drop_count_24h: 23,
    },
  };
})();

// 5. helper：路径分析（从 page X 出发，后续 page top N）
export function getPathFlowFromPage(pageId: number): Array<{ next_page_id: number; next_url: string; next_title: string; count: number; pct: number }> {
  const flows: Record<number, number> = {};
  let totalSourceVisits = 0;
  for (const s of idpSessionAggs) {
    for (let i = 0; i < s.page_path.length - 1; i++) {
      if (s.page_path[i].page_id === pageId) {
        totalSourceVisits++;
        const nextId = s.page_path[i + 1].page_id;
        flows[nextId] = (flows[nextId] || 0) + 1;
      }
    }
  }
  return Object.entries(flows)
    .map(([nextId, count]) => {
      const p = idpPages.find(p => p.id === Number(nextId));
      return {
        next_page_id: Number(nextId),
        next_url: p?.url || '',
        next_title: p?.title || '',
        count: count as number,
        pct: totalSourceVisits > 0 ? (count as number) / totalSourceVisits : 0,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
}

// 6. helper：退出页 Top N
export function getExitPagesTopN(n: number = 8): Array<{ page_id: number; url: string; title: string; exits: number; pct: number }> {
  const exits: Record<number, number> = {};
  let total = 0;
  for (const s of idpSessionAggs) {
    if (!s.exit_page_id) continue;
    exits[s.exit_page_id] = (exits[s.exit_page_id] || 0) + 1;
    total++;
  }
  return Object.entries(exits)
    .map(([pid, c]) => {
      const p = idpPages.find(p => p.id === Number(pid));
      return {
        page_id: Number(pid),
        url: p?.url || '',
        title: p?.title || '',
        exits: c as number,
        pct: total > 0 ? (c as number) / total : 0,
      };
    })
    .sort((a, b) => b.exits - a.exits)
    .slice(0, n);
}

// 7. helper：跨站点 device 串联（一个 device 出现在多个 site）
export function getCrossSiteDevices(): Array<{ device_id: string; sites: SiteId[]; sessions: number; first_seen: string; last_seen: string }> {
  const map: Record<string, { sites: Set<SiteId>; sessions: number; first: string; last: string }> = {};
  for (const s of idpSessionAggs) {
    if (!map[s.device_id]) {
      map[s.device_id] = { sites: new Set(), sessions: 0, first: s.start_at, last: s.start_at };
    }
    map[s.device_id].sites.add(s.site_id);
    map[s.device_id].sessions++;
    if (s.start_at < map[s.device_id].first) map[s.device_id].first = s.start_at;
    if (s.start_at > map[s.device_id].last) map[s.device_id].last = s.start_at;
  }
  return Object.entries(map)
    .filter(([_, v]) => v.sites.size > 1)
    .map(([d, v]) => ({
      device_id: d,
      sites: Array.from(v.sites),
      sessions: v.sessions,
      first_seen: v.first,
      last_seen: v.last,
    }))
    .sort((a, b) => b.sessions - a.sessions);
}

// 8. helper：engagement_ms 直方图
export function getEngagementDistribution(): Array<{ bucket: string; min_ms: number; max_ms: number; count: number; pct: number }> {
  const buckets = [
    { bucket: '<2s', min_ms: 0, max_ms: 2000, count: 0, pct: 0 },
    { bucket: '2-5s', min_ms: 2000, max_ms: 5000, count: 0, pct: 0 },
    { bucket: '5-10s', min_ms: 5000, max_ms: 10000, count: 0, pct: 0 },
    { bucket: '10-30s', min_ms: 10000, max_ms: 30000, count: 0, pct: 0 },
    { bucket: '30s-1m', min_ms: 30000, max_ms: 60000, count: 0, pct: 0 },
    { bucket: '>1m', min_ms: 60000, max_ms: Infinity, count: 0, pct: 0 },
  ];
  let total = 0;
  for (const e of idpEvents) {
    if (e.event_type !== 'page_view' || !e.engagement_ms) continue;
    total++;
    for (const b of buckets) {
      if (e.engagement_ms >= b.min_ms && e.engagement_ms < b.max_ms) {
        b.count++;
        break;
      }
    }
  }
  for (const b of buckets) b.pct = total > 0 ? b.count / total : 0;
  return buckets;
}

// 9. helper：DAU/WAU/MAU 趋势（基于 session 起始时间，按 device_id 计 unique）
export function getDauWauMau(): { dau: Array<{ date: string; users: number }>; wau: number; mau: number } {
  const dauMap: Record<string, Set<string>> = {};
  const last30Days = new Set<string>();
  const last7Days = new Set<string>();
  const today = new Date(2026, 5, 27);
  for (const s of idpSessionAggs) {
    const d = s.start_at.split('T')[0];
    if (!dauMap[d]) dauMap[d] = new Set();
    dauMap[d].add(s.device_id);
    const sessDate = new Date(s.start_at);
    const dayDiff = Math.floor((today.getTime() - sessDate.getTime()) / (24 * 3600 * 1000));
    if (dayDiff < 30) last30Days.add(s.device_id);
    if (dayDiff < 7) last7Days.add(s.device_id);
  }
  const dau = Object.entries(dauMap)
    .map(([date, users]) => ({ date, users: users.size }))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-14);
  return { dau, wau: last7Days.size, mau: last30Days.size };
}

// 10. helper：自定义漏斗计算（按 device_id 去重）
export function calcFunnel(steps: FunnelStep[]): FunnelStep[] {
  const sessionsByStep: Set<string>[] = steps.map(() => new Set());
  for (const s of idpSessionAggs) {
    let currentStep = 0;
    const events = idpEvents.filter(e => e.session_id === s.session_id);
    for (const e of events) {
      const step = steps[currentStep];
      const matchEvent = !step.match.event_type || e.event_type === step.match.event_type;
      const matchPage = !step.match.page_type ||
        (e.page_id && idpPages.find(p => p.id === e.page_id)?.page_type === step.match.page_type);
      if (matchEvent && matchPage) {
        sessionsByStep[currentStep].add(s.device_id);
        currentStep++;
        if (currentStep >= steps.length) break;
      }
    }
  }
  return steps.map((s, i) => ({
    ...s,
    user_count: sessionsByStep[i].size,
    drop_off_pct: i === 0 ? 0 : sessionsByStep[i - 1].size > 0
      ? 1 - sessionsByStep[i].size / sessionsByStep[i - 1].size
      : 0,
  }));
}

// 11. helper：实时事件流（最新 N 条）
export function getRealtimeEvents(n: number = 100): IdpEvent[] {
  return idpEvents.slice().sort((a, b) => b.event_time.localeCompare(a.event_time)).slice(0, n);
}

// 12. helper：viewport 桶热区
export function getViewportBucketHeatmap(): Record<DeviceType, Array<{ x: number; y: number; intensity: number }>> {
  const result: Record<string, Array<{ x: number; y: number; intensity: number }>> = { mobile: [], desktop: [], tablet: [] };
  const buckets: Record<string, Record<string, number>> = { mobile: {}, desktop: {}, tablet: {} };
  for (const e of idpEvents) {
    if (e.event_type !== 'click' || e.x_pct === undefined || e.y_pct === undefined) continue;
    const xBucket = Math.floor(e.x_pct / 10) * 10;
    const yBucket = Math.floor(e.y_pct / 10) * 10;
    const key = `${xBucket}_${yBucket}`;
    buckets[e.device_type][key] = (buckets[e.device_type][key] || 0) + 1;
  }
  for (const dt of Object.keys(buckets)) {
    const maxCount = Math.max(...Object.values(buckets[dt]), 1);
    for (const [key, count] of Object.entries(buckets[dt])) {
      const [x, y] = key.split('_').map(Number);
      result[dt].push({ x, y, intensity: count / maxCount });
    }
  }
  return result as Record<DeviceType, Array<{ x: number; y: number; intensity: number }>>;
}

// 13. helper：scroll 完读率
export function getScrollReadRate(): Array<{ page_id: number; title: string; url: string; reached_100_pct: number; topBounce_pct: number }> {
  const map: Record<number, { total: number; reach100: number; bounce: number }> = {};
  for (const r of scrollDailyRollups) {
    if (!map[r.page_id]) map[r.page_id] = { total: 0, reach100: 0, bounce: 0 };
    if (r.scroll_bucket === 25) map[r.page_id].total += r.users;
    if (r.scroll_bucket === 100) map[r.page_id].reach100 += r.users;
  }
  // 顶部弃读 = 抵达 25% 用户数远高于 50%
  for (const r of scrollDailyRollups) {
    if (r.scroll_bucket === 50 && map[r.page_id]) {
      map[r.page_id].bounce = map[r.page_id].total - r.users;
    }
  }
  return Object.entries(map).map(([pid, v]) => {
    const p = idpPages.find(p => p.id === Number(pid));
    return {
      page_id: Number(pid),
      title: p?.title || '',
      url: p?.url || '',
      reached_100_pct: v.total > 0 ? v.reach100 / v.total : 0,
      topBounce_pct: v.total > 0 ? Math.max(0, v.bounce / v.total) : 0,
    };
  }).sort((a, b) => b.reached_100_pct - a.reached_100_pct);
}

// 桑基图数据构建 — 从起点出发的多步分流（替代直线漏斗）
export function buildSankeyData(opts: {
  startPageId?: number | 'any';        // 'any' / undefined = 任意入口
  startPageType?: string | 'any';      // 限定 PDP / COLLECTION / HOMEPAGE 起点类型；与 startPageId 互斥
  maxDepth?: number;                    // 默认 4
}): {
  nodes: Array<{ id: string; label: string; depth: number; users: number; pageType?: string }>;
  links: Array<{ source: string; target: string; value: number }>;
  totalStart: number;
} {
  const maxDepth = opts.maxDepth ?? 4;
  // 构造每个 session 的"路径节点序列"（page_view + 行为节点 atc/purchase/exit）
  const pathSequences: Array<Array<{ key: string; label: string; pageType: string }>> = [];
  for (const s of idpSessionAggs) {
    // 起点过滤
    if (opts.startPageId && opts.startPageId !== 'any') {
      if (s.entry_page_id !== opts.startPageId) continue;
    } else if (opts.startPageType && opts.startPageType !== 'any') {
      const entryMeta = idpPages.find(p => p.id === s.entry_page_id);
      if (entryMeta?.page_type !== opts.startPageType) continue;
    }
    const seq: Array<{ key: string; label: string; pageType: string }> = [];
    // 把 page_path 每一步转为节点；遇到 atc/purchase 也作节点
    const events = idpEvents.filter(e => e.session_id === s.session_id).sort((a, b) => a.event_time.localeCompare(b.event_time));
    for (const e of events) {
      if (e.event_type === 'page_view' && e.page_id) {
        const p = idpPages.find(p => p.id === e.page_id);
        if (p) seq.push({ key: `page_${p.id}`, label: p.title, pageType: p.page_type });
      } else if (e.event_type === 'add_to_cart') {
        seq.push({ key: 'action_atc', label: '加购', pageType: 'ACTION' });
      } else if (e.event_type === 'checkout_start') {
        seq.push({ key: 'action_checkout', label: '结账', pageType: 'ACTION' });
      } else if (e.event_type === 'purchase') {
        seq.push({ key: 'action_purchase', label: '购买', pageType: 'ACTION' });
      } else if (e.event_type === 'inquiry_open') {
        seq.push({ key: 'action_inquiry', label: '询单', pageType: 'ACTION' });
      }
    }
    if (seq.length > 0) pathSequences.push(seq);
  }

  // 收节点 + 链接（按 depth × node 唯一）
  const nodeMap: Record<string, { id: string; label: string; depth: number; users: number; pageType?: string }> = {};
  const linkMap: Record<string, { source: string; target: string; value: number }> = {};

  for (const seq of pathSequences) {
    const cappedSeq = seq.slice(0, maxDepth);
    for (let i = 0; i < cappedSeq.length; i++) {
      const node = cappedSeq[i];
      const nodeId = `d${i}_${node.key}`;
      if (!nodeMap[nodeId]) {
        nodeMap[nodeId] = { id: nodeId, label: node.label, depth: i, users: 0, pageType: node.pageType };
      }
      nodeMap[nodeId].users++;
      if (i > 0) {
        const prev = cappedSeq[i - 1];
        const prevId = `d${i - 1}_${prev.key}`;
        const linkId = `${prevId}__${nodeId}`;
        if (!linkMap[linkId]) {
          linkMap[linkId] = { source: prevId, target: nodeId, value: 0 };
        }
        linkMap[linkId].value++;
      }
    }
    // 流失节点（如果 sequence 短于 maxDepth，加 "退出" 节点到下一深度）
    if (cappedSeq.length < maxDepth && cappedSeq.length > 0) {
      const last = cappedSeq[cappedSeq.length - 1];
      const lastId = `d${cappedSeq.length - 1}_${last.key}`;
      const exitId = `d${cappedSeq.length}_exit`;
      if (!nodeMap[exitId]) {
        nodeMap[exitId] = { id: exitId, label: '退出', depth: cappedSeq.length, users: 0, pageType: 'EXIT' };
      }
      nodeMap[exitId].users++;
      const linkId = `${lastId}__${exitId}`;
      if (!linkMap[linkId]) {
        linkMap[linkId] = { source: lastId, target: exitId, value: 0 };
      }
      linkMap[linkId].value++;
    }
  }
  return {
    nodes: Object.values(nodeMap),
    links: Object.values(linkMap),
    totalStart: pathSequences.length,
  };
}

// 14. helper：单 session 事件链
export function getSessionEvents(sessionId: string): IdpEvent[] {
  return idpEvents.filter(e => e.session_id === sessionId).sort((a, b) => a.event_time.localeCompare(b.event_time));
}

// 15. helper：实验 × 候选交叉
export function getExperimentCandidateCrosstab(experimentId: string): Array<{
  candidate_id: string; title: string; variant_id: string; variant_key: string; exposures: number; conversions: number; rate: number;
}> {
  const map: Record<string, { exposures: number; conversions: number; variantKey: string }> = {};
  for (const e of idpEvents) {
    if (e.experiment_id !== experimentId || !e.candidate_id || !e.variant_id) continue;
    const key = `${e.candidate_id}__${e.variant_id}`;
    if (!map[key]) {
      const v = idpVariants.find(v => v.variant_id === e.variant_id);
      map[key] = { exposures: 0, conversions: 0, variantKey: v?.variant_key || '' };
    }
    if (e.event_type === 'page_view') map[key].exposures++;
    if (e.event_type === 'add_to_cart' || e.event_type === 'purchase') map[key].conversions++;
  }
  return Object.entries(map).map(([k, v]) => {
    const [candidate_id, variant_id] = k.split('__');
    const c = aiCandidates.find(c => c.candidate_id === candidate_id);
    return {
      candidate_id,
      title: c?.title || '',
      variant_id,
      variant_key: v.variantKey,
      exposures: v.exposures,
      conversions: v.conversions,
      rate: v.exposures > 0 ? v.conversions / v.exposures : 0,
    };
  }).sort((a, b) => b.exposures - a.exposures);
}

// ============================================================
// 反馈学习 mock(定价 / listing / 图片 / 效果回流)
// ============================================================

// 1. 定价学习 — 用真实代码 EpsCommodity/EpsFactoryOrderSuggestedRetailPrice 体系字段
export const priceLearningSnapshot: PriceLearningSnapshot = {
  acceptance: {
    multiplier_mae_pct: 12.4,         // 目标 <15% ✓
    has_evidence_rate: 1.00,          // 100% 带依据 ✓
    gold_subscriber_lag_minutes: 0.8,  // 分钟级 ✓
    accept_band_hit_rate: 0.83,       // ≥80% ✓
  },
  site_regression: [
    {
      site: 'FG',
      sample_count: 482,
      mae_pct: 11.2,
      accept_band_hit: 0.87,
      avg_confidence: 0.81,
      notable_misses: [
        { commodity_no: 'ZB-FG-184223', real_usd: 248.00, ai_recommend_usd: 198.00, deviation_pct: -20.2, reason_guess: '主石规格估低 0.18ct → 0.25ct' },
        { commodity_no: 'ZB-FG-187452', real_usd: 158.00, ai_recommend_usd: 208.00, deviation_pct: 31.6, reason_guess: '该款历史多次促销价污染倍率训练集' },
        { commodity_no: 'ZB-FG-189117', real_usd: 388.00, ai_recommend_usd: 268.00, deviation_pct: -30.9, reason_guess: '工艺复杂(微镶 + 钻台镂空)未在 K 近邻特征覆盖' },
      ],
    },
    {
      site: 'ST',
      sample_count: 316,
      mae_pct: 14.1,
      accept_band_hit: 0.79,
      avg_confidence: 0.74,
      notable_misses: [
        { commodity_no: 'ZB-ST-082341', real_usd: 178.00, ai_recommend_usd: 138.00, deviation_pct: -22.5, reason_guess: 'ST 站定价多 +20% 加价（市场策略）AI 未学到' },
        { commodity_no: 'ZB-ST-084112', real_usd: 218.00, ai_recommend_usd: 268.00, deviation_pct: 22.9, reason_guess: '过度借用 FG 站高溢价相似款' },
      ],
    },
    {
      site: 'BF',
      sample_count: 254,
      mae_pct: 13.7,
      accept_band_hit: 0.82,
      avg_confidence: 0.77,
      notable_misses: [
        { commodity_no: 'ZB-BF-063215', real_usd: 168.00, ai_recommend_usd: 138.00, deviation_pct: -17.9, reason_guess: 'BF 站近月银价波动未及时反映' },
      ],
    },
  ],
  override_by_style: [
    { style: '哥特', total: 124, overridden: 18, rate: 0.145, avg_deviation_pct: 8.2 },
    { style: '暗黑', total: 98, overridden: 21, rate: 0.214, avg_deviation_pct: 12.5 },
    { style: '森系', total: 87, overridden: 14, rate: 0.161, avg_deviation_pct: 9.1 },
    { style: '古典', total: 115, overridden: 31, rate: 0.270, avg_deviation_pct: 14.8 },
    { style: '极简', total: 142, overridden: 12, rate: 0.085, avg_deviation_pct: 5.4 },
    { style: '复古', total: 76, overridden: 16, rate: 0.211, avg_deviation_pct: 11.3 },
  ],
  override_by_price_range: [
    { range: '$0-100', min_usd: 0, max_usd: 100, count: 38, overridden: 14, rate: 0.368 },
    { range: '$100-200', min_usd: 100, max_usd: 200, count: 285, overridden: 32, rate: 0.112 },
    { range: '$200-400', min_usd: 200, max_usd: 400, count: 224, overridden: 41, rate: 0.183 },
    { range: '$400-800', min_usd: 400, max_usd: 800, count: 78, overridden: 18, rate: 0.231 },
    { range: '$800+', min_usd: 800, max_usd: 9999, count: 17, overridden: 7, rate: 0.412 },
  ],
  knn_example: {
    candidate_id: 'cand_0007',
    title: '复古玫瑰金叶纹手镯',
    similar_items: [
      { ref_id: 'eps_commodity_88421', commodity_no: 'ZB-FG-178234', similarity: 0.91, pure_gold_weight_g: 3.42, main_stone_value_cents: 8500, slave_stone_value_cents: 1200, multiplier: 320, weight_in_estimate: 0.45 },
      { ref_id: 'eps_commodity_88573', commodity_no: 'ZB-FG-179012', similarity: 0.87, pure_gold_weight_g: 3.18, main_stone_value_cents: 7800, slave_stone_value_cents: 1450, multiplier: 315, weight_in_estimate: 0.32 },
      { ref_id: 'eps_commodity_88712', commodity_no: 'ZB-ST-091824', similarity: 0.82, pure_gold_weight_g: 3.55, main_stone_value_cents: 9200, slave_stone_value_cents: 900, multiplier: 308, weight_in_estimate: 0.23 },
    ],
    estimated: { pure_gold_weight_g: 3.34, main_stone_value_cents: 8398, slave_stone_value_cents: 1225 },
  },
};

// 2. listing RAG 学习
export const listingLearningSnapshot: ListingLearningSnapshot = {
  recall_pool_total: 1842,
  high_weight_count: 312,
  blacklist_count: 47,
  avg_similarity: 0.78,
  avg_neighbor_count: 3.2,
  high_weight_samples: [
    { listing_id: 'list_hi_001', candidate_id: 'cand_0022', title: 'Gothic Cathedral Vintage Ring 14K Gold Spire Detail', style: '哥特', outcome: 'HIT', weight: 0.92, reach_count_7d: 48, source: 'HIT_RECAP', conversion_rate: 0.057, highlight_excerpt: 'Handcrafted spire-detail Gothic ring inspired by 13th-century cathedral architecture...' },
    { listing_id: 'list_hi_002', candidate_id: 'cand_0014', title: 'Dark Romance Rose Thorn Bracelet Sterling Silver', style: '暗黑', outcome: 'HIT', weight: 0.88, reach_count_7d: 36, source: 'AB_WINNER', conversion_rate: 0.063, highlight_excerpt: 'Thorned rose vine wraps the wrist in delicate sterling silver — every twist hand-set...' },
    { listing_id: 'list_hi_003', candidate_id: 'cand_0027', title: 'Forest Floor Fern Brooch Brass Botanical', style: '森系', outcome: 'HIT', weight: 0.85, reach_count_7d: 31, source: 'HIT_RECAP', conversion_rate: 0.049, highlight_excerpt: 'Inspired by Pacific Northwest fern fronds, this brooch celebrates botanical asymmetry...' },
    { listing_id: 'list_hi_004', candidate_id: 'cand_0023', title: 'Minimalist Solid Gold Stack Ring 14K Set of 3', style: '极简', outcome: 'HIT', weight: 0.81, reach_count_7d: 27, source: 'AB_WINNER', conversion_rate: 0.071, highlight_excerpt: 'Three weights, one statement. Stack solo or together — pure 14K, no plating...' },
  ],
  blacklist_samples: [
    { listing_id: 'list_lo_001', candidate_id: 'cand_archived_x1', title: 'Beautiful nice gothic ring with great design', style: '哥特', outcome: 'MISS', weight: 0.08, reach_count_7d: 0, source: 'BASELINE', conversion_rate: 0.008, highlight_excerpt: '<empty / generic adjective stuffing / no specific craft detail>' },
    { listing_id: 'list_lo_002', candidate_id: 'cand_archived_x2', title: 'Gothic ring style 14k available now order', style: '哥特', outcome: 'MISS', weight: 0.05, reach_count_7d: 0, source: 'BASELINE', conversion_rate: 0.003, highlight_excerpt: '<keyword stuffing without semantic flow>' },
  ],
  edit_patterns: [
    { field: 'title', edit_count_30d: 87, edit_rate: 0.42, common_pattern: '补充材质 + 风格关键词在前 5 个词', example_diff: { before: 'Gothic Ring 14K Gold Solid', after: '14K Solid Gold Gothic Cathedral Ring' } },
    { field: 'seo_desc', edit_count_30d: 64, edit_rate: 0.31, common_pattern: '加入工艺细节 + brand story 前 80 字符', example_diff: { before: 'Beautiful gothic style ring at Felicegals.', after: 'Hand-set 14K gold gothic ring with cathedral spire detail. Felicegals — small-batch.' } },
    { field: 'description_html', edit_count_30d: 52, edit_rate: 0.25, common_pattern: '运营把 AI 写的"made with premium materials"改成具体材质 / 工艺名词', example_diff: { before: 'Made with premium materials and exquisite craft.', after: 'Cast in 14K solid gold using lost-wax technique; spire detail hand-finished.' } },
    { field: 'tags', edit_count_30d: 38, edit_rate: 0.18, common_pattern: '删除过于宽泛 tag(ring/jewelry)；保留具体风格 + 材质 tag', example_diff: { before: '["ring", "gold", "jewelry", "gothic"]', after: '["gothic", "cathedral", "14k-gold", "lost-wax"]' } },
    { field: 'collections', edit_count_30d: 12, edit_rate: 0.06, common_pattern: '运营较少改 collections', example_diff: { before: '["哥特系列", "新品"]', after: '["哥特系列", "新品"]' } },
  ],
  prompt_version_history: [
    { version: 'v1.0', rolled_out_at: '2026-02-15', accept_rate_first_round: 0.41, ab_winning_count: 0, high_weight_added: 12 },
    { version: 'v1.3', rolled_out_at: '2026-03-22', accept_rate_first_round: 0.48, ab_winning_count: 2, high_weight_added: 38 },
    { version: 'v1.5', rolled_out_at: '2026-04-18', accept_rate_first_round: 0.55, ab_winning_count: 5, high_weight_added: 67 },
    { version: 'v1.7', rolled_out_at: '2026-06-02', accept_rate_first_round: 0.62, ab_winning_count: 8, high_weight_added: 89 },
  ],
};

// 3. 图片学习（戒指单品类）
export const imageLearningSnapshot: ImageLearningSnapshot = {
  reject_aggregation: [
    { reason_code: 'BLURRY', count_30d: 187, rate: 0.21, trend: 'down', feedback_to: 'prompt_template' },
    { reason_code: 'CHAOTIC_RING', count_30d: 142, rate: 0.16, trend: 'down', feedback_to: 'prompt_template' },
    { reason_code: 'STONE_OFF', count_30d: 124, rate: 0.14, trend: 'flat', feedback_to: 'prompt_template' },
    { reason_code: 'HAND_DEFORMED', count_30d: 98, rate: 0.11, trend: 'flat', feedback_to: 'prompt_template' },
    { reason_code: 'COLOR_OFF', count_30d: 76, rate: 0.085, trend: 'down', feedback_to: 'prompt_template' },
    { reason_code: 'PROP_DEFECT', count_30d: 48, rate: 0.054, trend: 'flat', feedback_to: 'prompt_template' },
    { reason_code: 'BACKGROUND_BAD', count_30d: 42, rate: 0.047, trend: 'down', feedback_to: 'prompt_template' },
    { reason_code: 'STYLE_MISMATCH', count_30d: 156, rate: 0.175, trend: 'up', feedback_to: 'selection' },
    { reason_code: 'OTHER', count_30d: 18, rate: 0.020, trend: 'flat', feedback_to: 'both' },
  ],
  cv_filter: { accuracy: 0.92, false_kill_rate: 0.038, samples_30d: 4218 },
  draw_threshold: {
    category: 'ring',
    spec_baseline: 3,
    current_active: 4,
    suggested_next: 4,
    reason: 'CHAOTIC_RING 拒率连续 3 周降至 16% 但 STYLE_MISMATCH 上升 → 抽 4 张换运营挑选余量更稳，不再降到 3 张',
  },
  selection_downweight: [
    { style_subtag: '哥特蛛网纹（精细线条）', total_attempts: 42, pass_count: 4, pass_rate: 0.095, flagged_at: daysAgo(8) },
    { style_subtag: '暗黑骷髅细节（眼眶/牙齿）', total_attempts: 38, pass_count: 6, pass_rate: 0.158, flagged_at: daysAgo(12) },
    { style_subtag: '微镶+钻台镂空（极高复杂度）', total_attempts: 28, pass_count: 1, pass_rate: 0.036, flagged_at: daysAgo(5) },
  ],
};

// 4. 效果回流（→ 选款）
export const effectFeedbackSnapshot: EffectFeedbackSnapshot = {
  outcome_by_style: [
    { style: '哥特', total: 24, hit: 11, miss: 7, partial: 6, hit_rate: 0.458 },
    { style: '暗黑', total: 19, hit: 6, miss: 9, partial: 4, hit_rate: 0.316 },
    { style: '森系', total: 18, hit: 9, miss: 4, partial: 5, hit_rate: 0.500 },
    { style: '古典', total: 14, hit: 4, miss: 7, partial: 3, hit_rate: 0.286 },
    { style: '极简', total: 21, hit: 13, miss: 3, partial: 5, hit_rate: 0.619 },
    { style: '复古', total: 16, hit: 6, miss: 6, partial: 4, hit_rate: 0.375 },
  ],
  outcome_by_model: [
    { model: 'NB Pro', total: 41, hit_rate: 0.488, avg_cvr: 0.041 },
    { model: 'NB2', total: 32, hit_rate: 0.406, avg_cvr: 0.035 },
    { model: 'Flux 2 + jewelry LoRA', total: 26, hit_rate: 0.538, avg_cvr: 0.046 },
    { model: 'GPT Image 2', total: 13, hit_rate: 0.231, avg_cvr: 0.024 },
  ],
  source_type_weight_adjust: [
    { source_type: 'TREND', current_weight: 0.30, hit_rate_30d: 0.52, suggested_weight: 0.38, delta: +0.08, reason: '趋势来源命中率高，加权' },
    { source_type: 'STORE_SCAN', current_weight: 0.30, hit_rate_30d: 0.41, suggested_weight: 0.32, delta: +0.02, reason: '基线持平' },
    { source_type: 'COMPETITOR', current_weight: 0.25, hit_rate_30d: 0.28, suggested_weight: 0.18, delta: -0.07, reason: '同类已饱和，抄竞品命中率低，降权' },
    { source_type: 'INQUIRY_TOPIC', current_weight: 0.15, hit_rate_30d: 0.45, suggested_weight: 0.12, delta: -0.03, reason: '样本少，但命中率高，保持基线' },
  ],
  feedback_loop_flow: [
    { from: 'PUBLISHED', to: 'MEASURING', weight: 100, semantic: '所有发布候选进观察期' },
    { from: 'MEASURING', to: 'LEARNED', weight: 73, semantic: '7-30 天观察后复盘' },
    { from: 'LEARNED (HIT)', to: '选款·风格加权', weight: 49, semantic: 'HIT 风格反馈风格权重' },
    { from: 'LEARNED (HIT)', to: '选款·AI 模型加权', weight: 49, semantic: 'HIT 用过的 AI 模型加权' },
    { from: 'LEARNED (HIT)', to: 'listing 召回库·高权重池', weight: 49, semantic: '高转化 listing 进 RAG 高权重池' },
    { from: 'LEARNED (MISS)', to: '选款·风格降权', weight: 36, semantic: 'MISS 风格反馈风格降权' },
    { from: 'LEARNED (MISS)', to: 'listing 召回库·黑名单', weight: 36, semantic: '失败 listing 进 RAG 黑名单' },
    { from: 'LEARNED (PARTIAL)', to: '选款·待观察', weight: 27, semantic: 'PARTIAL 留观察期再二次复盘' },
  ],
};
